/**
 * Sesión registrada.
 *
 * Un baseline ES una sesión. Un checkpoint TAMBIÉN (cambio aprobado #3): tienen
 * pasos, duración, grabación y notas. Mantenerlos como tablas separadas
 * duplicaría toda la lógica de reanudación, temporizador y completitud.
 *
 * Los pasos van EMBEBIDOS en la fila (cambio aprobado #4). El argumento no es
 * de escala sino de corrección: al volver de ChatGPT el estado tiene que estar
 * íntegro, y en dos tablas una escritura interrumpida deja la sesión diciendo
 * «paso 3» mientras los pasos dicen que el 3 ya terminó.
 * Una fila = una escritura atómica = imposible quedar a medias.
 */

import type { DuracionSesion } from './ajustes.js';
import type { Area, AreaOpcional } from './areas.js';
import type { Kind } from './banco.js';
import type { Prioridad } from './prioridad.js';
import type { Id } from './ids.js';
import type { MetadatosSync } from './sincronizacion.js';

export type TipoSesion = 'baseline' | 'dia' | 'checkpoint';

/**
 * Estado de la sesión.
 *
 * `en_progreso` es lo que permite reanudar. Una sesión NUNCA pasa a
 * `completada` solo por haberse abierto (CA-3.8).
 */
export const ESTADOS_SESION = [
  'en_progreso',
  'completada',
  'parcial',
  'abandonada',
] as const;
export type EstadoSesion = (typeof ESTADOS_SESION)[number];

export type SemanaCheckpoint = 0 | 4 | 8 | 12;

/**
 * Registro de lo que pasó en un paso concreto.
 *
 * EL TEMPORIZADOR ES UNA SUMA DE SEGMENTOS, NO UN CONTADOR.
 *
 * `segundosAcumulados` guarda los segmentos ya cerrados y `corriendoDesde` marca
 * el inicio del segmento en curso. El tiempo transcurrido se CALCULA:
 *
 *     transcurrido = segundosAcumulados + (ahora − corriendoDesde)
 *
 * Así sobrevive a que JavaScript deje de ejecutarse: da igual que Android
 * estrangule la pestaña, que el proceso muera o que el usuario pase 25 minutos
 * en ChatGPT Voice. El timestamp es la verdad; nunca hay un `setInterval` del
 * que dependa la cuenta.
 */
export interface PasoRegistrado {
  /** `clave` del paso en el curriculum. */
  stepKey: string;
  completado: boolean;
  omitido: boolean;
  motivoOmision?: string;
  /** Cuándo se entró al paso por primera vez. */
  iniciadoEn?: string;
  /**
   * Inicio del segmento de tiempo en curso.
   * `null` o ausente = el temporizador está pausado o no ha empezado.
   */
  corriendoDesde?: string | null;
  /** Segundos de los segmentos ya cerrados. */
  segundosAcumulados: number;
  notas?: string;
}

/**
 * Una corrección del TOP 3.
 *
 * `noConfiable` existe solo para pronunciación: ChatGPT no es un instrumento
 * fonético, y obligar a inventar una corrección de pronunciación sería peor
 * que no tener ninguna.
 */
export interface CorreccionTop {
  /**
   * La corrección tal como el usuario la escribe.
   *
   * Es el TARGET: lo que se esconde en el Día 4 y se revela al pulsar
   * «Mostrar». Se guarda íntegra y sin analizar —la aplicación no parte
   * «say X, not Y» en dos mitades—, pero como respuesta, no como contexto.
   */
  texto: string;
  /**
   * Qué estaba intentando decir, si lo aporta. OPCIONAL.
   *
   * Es lo único que hace del elemento un ejercicio de retrieval de verdad:
   * sin contexto hay algo que revelar, pero nada desde donde recuperarlo.
   * Nunca se deduce del texto de la corrección.
   */
  contexto?: string;
  /** El usuario pidió llevarla al Learning Bank. */
  alBanco: boolean;
  /** Solo para pronunciación: «no se puede juzgar con confianza». */
  noConfiable?: boolean;
}

export interface CorreccionesTop3 {
  pronunciacion: CorreccionTop;
  naturalidad: CorreccionTop;
  gramatica: CorreccionTop;
}

export function correccionVacia(): CorreccionTop {
  return { texto: '', alBanco: false };
}

export function correccionesVacias(): CorreccionesTop3 {
  return {
    pronunciacion: { ...correccionVacia(), noConfiable: false },
    naturalidad: correccionVacia(),
    gramatica: correccionVacia(),
  };
}

/**
 * Una dificultad del cierre del baseline, tal como se está escribiendo.
 *
 * Vive en el MODELO y no en el componente porque se persiste: si solo
 * existiera como estado de React, recargar a mitad de escribirla la perdería.
 */
export interface DificultadRegistrada {
  /** Identidad estable: la posición en la lista no sirve como identidad. */
  id: Id;
  contexto: string;
  kind: Kind;
  area: AreaOpcional;
  correccionTarget: string;
  prioridad: Prioridad;
}

export interface SesionRegistrada extends MetadatosSync {
  id: Id;
  tipo: TipoSesion;
  /** Clave de la sesión en el curriculum: 'baseline' | 'd1'..'d7' | 'checkpoint'. */
  sessionKey: string;
  semana: number;
  /** Solo en baseline (0) y checkpoints (4, 8, 12). */
  checkpointSemana?: SemanaCheckpoint;
  duracionPlanificada: DuracionSesion;
  segundosReales: number;
  iniciadaEn: string;
  completadaEn?: string;
  estado: EstadoSesion;
  pasoActual: number;
  pasos: PasoRegistrado[];
  /** Foco elegido para el Día 5. */
  focoDia5?: Area;
  /** Target activo que se estaba trabajando (YouGlish, Accent Coach). */
  targetActivo?: string;
  /** Autoevaluaciones 1-5 de las áreas relevantes a esta sesión. */
  autoevaluaciones: Partial<Record<Area, number>>;
  /** Confianza 1-5, separada de las áreas: es un estado, no una habilidad. */
  confianza?: number;
  /** «¿Qué fue lo más difícil hoy?», una frase. */
  masDificil?: string;
  /**
   * Área que el usuario marcó como lo más difícil.
   *
   * Estructurada a propósito: es una señal directa del Día 5, y un texto libre
   * no se puede contar.
   */
  masDificilArea?: Area;
  correccionesTop3?: CorreccionesTop3;
  /**
   * Feedback de ChatGPT pegado tal cual.
   *
   * Solo referencia. En la V1 NO se interpreta con IA ni con expresiones
   * regulares: las correcciones estructuradas las confirma el usuario a mano,
   * que es más lento pero no inventa categorías.
   */
  feedbackChatGPT?: string;
  /**
   * Día 6: si la conversación fue con una persona real.
   *
   * Se registra aparte de `usoRespaldoIA` a propósito: la aplicación nunca
   * puede dar por equivalente hablar con ChatGPT y hablar con alguien.
   */
  conPersonaReal?: boolean;
  /** Día 6: si se usó ChatGPT como respaldo. */
  usoRespaldoIA?: boolean;
  /**
   * Qué fuente de listening se usó en esta medición.
   *
   * Solo en baseline y checkpoints. Se guarda para NO repetirla en la
   * siguiente: si ya escuchaste ese material, la medición deja de medir
   * comprensión y pasa a medir memoria.
   */
  fuenteListeningClave?: string;
  /**
   * Resumen que ChatGPT dio al final del checkpoint, pegado a mano.
   *
   * Se guarda tal cual, como referencia longitudinal. La aplicación NO lo
   * interpreta ni saca métricas de él: lo que se compara son los datos que el
   * usuario confirmó, no una lectura automática de un texto.
   */
  resumenChatGPT?: string;
  /**
   * Dificultades del baseline mientras se escriben.
   *
   * Es un BORRADOR: existir aquí no crea nada en el Learning Bank. La siembra
   * ocurre solo cuando el usuario confirma el cierre.
   */
  borradorDificultades?: DificultadRegistrada[];
  /** Área que la aplicación recomendó para el Día 5, antes de decidir. */
  focoRecomendado?: Area;
  /** `true` si el usuario eligió un foco distinto del recomendado. */
  focoManual?: boolean;
  grabacionIds: Id[];
  /**
   * Instantánea del material del Día 4.
   *
   * Se fija al empezar el paso de retrieval y NO se recalcula: si el usuario
   * edita el banco a mitad de sesión, el material de ESTA sesión no cambia
   * debajo de sus pies.
   */
  elementosSeleccionados?: Id[];
  /** Cómo le fue con cada elemento practicado. */
  practicaElementos?: Record<Id, 'facil' | 'costo' | 'no_salio'>;
  /** Versión del curriculum con la que se ejecutó (CA-3.7). */
  curriculumVersion: string;
}

/** Una sesión que todavía está abierta y se puede reanudar. */
export function estaEnProgreso(sesion: SesionRegistrada): boolean {
  return sesion.estado === 'en_progreso' && !sesion.deleted;
}

/** Cuenta como «hecha» para la progresión: completada o parcial. */
export function cuentaComoHecha(sesion: SesionRegistrada): boolean {
  return !sesion.deleted && (sesion.estado === 'completada' || sesion.estado === 'parcial');
}
